import { zodResolver } from "@hookform/resolvers/zod";
import { Grid } from "@mui/material";
import { useForm } from "react-hook-form";

import { BlFormInput } from "Forms/Components/BlFormInput";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  type CreateJobFormData,
  CreateJobFormSchema,
} from "Projects/Forms/createJobFormSchema";
import { BlModal } from "Shared/Dialogs/BlModal";

interface CreateJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (jobData: CreateJobFormData) => void;
}

export const CreateJobModal = ({
  isOpen,
  onClose,
  onConfirm,
}: CreateJobModalProps): React.ReactNode => {
  const { t } = useResource();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateJobFormData>({
    resolver: zodResolver(CreateJobFormSchema),
    defaultValues: {
      name: "",
    },
  });

  const onSubmit = (data: CreateJobFormData) => {
    onConfirm(data);
    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Projects.CreateJobModal.Title)}
      onCancel={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.Projects.CreateJobModal.Create)}
      maxWidth="500px"
    >
      <Grid container spacing={Spacing.M}>
        <Grid size={12}>
          <BlFormInput
            control={control}
            name="name"
            label={t(Resources.Projects.CreateJobModal.Name)}
            errors={errors}
            placeholder={t(Resources.Projects.CreateJobModal.NamePlaceholder)}
            required
          />
        </Grid>
      </Grid>
    </BlModal>
  );
};

// Export the form data type for backward compatibility
export type { CreateJobFormData } from "Projects/Forms/createJobFormSchema";
